import { Component, inject, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { OfflineService } from '../../../../services/pwa/offline.service';
import { SyncQueueService } from '../../../../services/pwa/sync-queue.service';
import { environment } from '../../../../../environments/environment';

@Component({
  selector: 'lc-sync-status',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './sync-status.component.html',
  styleUrl: './sync-status.component.scss'
})
export class SyncStatusComponent {
  private readonly offlineService = inject(OfflineService);
  private readonly syncQueueService = inject(SyncQueueService);

  // Signals computados
  readonly isOffline = this.offlineService.isOffline;
  readonly pendingActionsCount = this.offlineService.pendingActionsCount;
  readonly syncProgress = this.offlineService.syncProgress;
  readonly lastSync = this.offlineService.lastSync;
  readonly isProcessing = this.syncQueueService.isProcessing;
  readonly queueSize = this.syncQueueService.queueSize;

  // Estado local
  private isSyncingState = false;
  environment: any = environment;

  /**
   * Verifica se está sincronizando
   */
  isSyncing(): boolean {
    return this.isSyncingState || this.isProcessing();
  }

  /**
   * Obtém texto do status da última sincronização
   */
  getLastSyncText(): string {
    if (!this.lastSync()) {
      return 'Nunca sincronizado';
    }

    const now = new Date();
    const lastSyncTime = this.lastSync()!;
    const diffInMinutes = Math.floor((now.getTime() - lastSyncTime.getTime()) / (1000 * 60));

    if (diffInMinutes < 1) {
      return 'Última sincronização: agora mesmo';
    } else if (diffInMinutes === 1) {
      return 'Última sincronização: 1 minuto atrás';
    } else if (diffInMinutes < 60) {
      return `Última sincronização: ${diffInMinutes} minutos atrás`;
    } else {
      const diffInHours = Math.floor(diffInMinutes / 60);
      if (diffInHours === 1) {
        return 'Última sincronização: 1 hora atrás';
      } else {
        return `Última sincronização: ${diffInHours} horas atrás`;
      }
    }
  }

  /**
   * Obtém texto do botão principal
   */
  getButtonText(): string {
    if (this.isOffline()) {
      return 'Offline';
    }

    if (this.isSyncing()) {
      return 'Sincronizando...';
    }

    if (this.pendingActionsCount() > 0) {
      return `Sincronizar (${this.pendingActionsCount()})`;
    }

    return 'Sincronizar Agora';
  }

  /**
   * Verifica se o botão está desabilitado
   */
  isButtonDisabled(): boolean {
    return this.isOffline() || this.isSyncing();
  }

  /**
   * Manipula clique no botão de sincronização
   */
  async handleSyncClick(): Promise<void> {
    if (this.isButtonDisabled()) {
      return;
    }

    this.isSyncingState = true;

    try {
      // Forçar sincronização
      await this.offlineService.forcarSincronizacao();
      
      // Log para desenvolvimento
    } catch (error) {
      console.error('Erro ao sincronizar:', error);
    } finally {
      this.isSyncingState = false;
    }
  }

  /**
   * Obtém estatísticas para debug
   */
  getDebugInfo(): any {
    return {
      isOffline: this.isOffline(),
      pendingActions: this.pendingActionsCount(),
      syncProgress: this.syncProgress(),
      queueSize: this.queueSize(),
      isProcessing: this.isProcessing(),
      lastSync: this.lastSync()
    };
  }
} 